<!-- Modal Backdrop -->
<div class="modal modal-open" (click)="closeModal()" [@fadeIn] role="dialog" aria-modal="true" aria-label="Course player" data-testid="learner-modal-modal">

  <!-- Full Modal Loading State - covers entire modal -->
  @if (isLoading) {
    <div
      class="modal-content h-full flex items-center justify-center"
      aria-busy="true"
      aria-live="polite"
      data-testid="learner-modal-loading"
      [class.fullscreen]="isFullscreen"
      (click)="$event.stopPropagation()"
      [@slideIn]>
      <button class="btn btn-ghost btn-sm absolute top-4 right-4"
        (click)="closeModal()"
        aria-label="Close"
        data-testid="learner-modal-loading-close-btn">
        <app-icon name="close" size="sm"></app-icon>
      </button>
      <div class="text-center" [@loadingPulse]>
        <div class="relative mb-6">
          <!-- Pulsing background circle -->
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="w-32 h-32 bg-primary/20 rounded-full animate-ping"></div>
          </div>
          <!-- Static icon -->
          <div class="relative">
            <app-icon name="school" [size]="64" class="text-primary"></app-icon>
          </div>
        </div>
        <h3 class="text-xl font-semibold mb-2">Loading Course</h3>
        <p class="text-base-content/70 mb-4">Preparing your learning experience...</p>
        <div class="flex justify-center gap-1">
          <div class="w-2 h-2 bg-primary rounded-full animate-bounce" style="animation-delay: 0ms"></div>
          <div class="w-2 h-2 bg-primary rounded-full animate-bounce" style="animation-delay: 150ms"></div>
          <div class="w-2 h-2 bg-primary rounded-full animate-bounce" style="animation-delay: 300ms"></div>
        </div>
      </div>
    </div>
  }

  <!-- Modal Content - only show when loaded -->
  @if (!isLoading) {
    <div
      class="modal-content h-full flex flex-col"
      [class.fullscreen]="isFullscreen"
      (click)="$event.stopPropagation()"
      #modalContent
      [@slideIn]
      [@contentFadeIn]>
      <!-- Instructor preview banner -->
      @if (playerMode === 'instructor_preview') {
        <!-- KAN-653: .modal-content is transparent, so these rows need their
             own opaque surface or the page behind the modal (the app
             sidebar's profile card) shows through and collides with the
             banner text. The warning tint is layered on top of base-100. -->
        <div class="preview-chrome shrink-0 bg-base-100" data-testid="learner-modal-preview-chrome">
          <div class="preview-banner flex items-center justify-between gap-3 px-4 py-2 bg-warning/10 border-b border-warning/30 text-sm text-base-content"
               data-testid="learner-modal-preview-banner">
            <div class="flex items-center gap-2">
              <app-icon name="visibility" size="sm"></app-icon>
              <span>
                Instructor preview —
                {{ previewUnlocked ? 'navigation unlocked for this session' : 'matching learner experience' }}
              </span>
            </div>
            <label class="cursor-pointer flex items-center gap-2">
              <span class="text-xs">Unlock all modules</span>
              <input type="checkbox"
                     class="toggle toggle-sm toggle-warning"
                     [checked]="previewUnlocked"
                     (change)="togglePreviewUnlock()"
                     data-testid="learner-modal-preview-unlock-toggle" />
            </label>
          </div>
          <!-- Consolidated instructor metadata strip (override + module rule).
               Only renders content when the current item is a module with
               requireVideoCompletion=true, or when the override applies. -->
          <app-instructor-preview-metadata-strip
            class="block px-4 pt-2"
            [overrideActive]="shouldShowOverrideChip()"
            [videoRequired]="!!currentItem?.requireVideoCompletion">
          </app-instructor-preview-metadata-strip>
        </div>
      }
      <div class="flex flex-1 min-h-0">
      <!-- Timeline Sidebar -->
      <div class="timeline-sidebar"
        [class.hidden]="!showTimeline"
        [class.mobile-overlay]="isMobile && showTimeline">
        @if (courseStructure) {
          <app-course-timeline
            [navigationItems]="courseStructure.navigationItems"
            [currentItemId]="currentItem?.id || ''"
            [courseName]="courseStructure.courseName"
            [playerMode]="playerMode"
            [courseComplete]="isCourseComplete"
            [courseProgressMap]="courseProgressMap"
            [coursePassingThreshold]="courseData?.passingPolicy?.coursePassingThreshold ?? null"
            [allowJumpNavigation]="playerConfig?.allowJumpNavigation || false"
            (onItemSelect)="onTimelineItemSelect($event)"
            (onClose)="onTimelineClose()">
          </app-course-timeline>
        }
      </div>
      <!-- Main Content Area -->
      <div class="content-area flex-1">
        <!-- Access Denied clean state (KAN-415): a departed/removed learner
             deep-linked an old course URL and the base read denied access. A
             retry cannot restore revoked access, so we show a friendly message
             and a single "Back to My Courses" action instead of the generic
             "Error Loading Course" + Retry crash. -->
        @if (accessDenied && !isLoading) {
          <div class="error-state h-full flex items-center justify-center bg-base-200" data-testid="learner-modal-access-denied-section">
            <div class="text-center max-w-md p-8">
              <app-icon name="lock" class="text-warning mb-4" size="lg"></app-icon>
              <h3 class="text-xl font-bold mb-2">Access No Longer Available</h3>
              <p class="text-base-content/70 mb-4">{{ accessDeniedMessage }}</p>
              <div class="flex gap-2 justify-center">
                <button class="btn btn-primary" (click)="returnToMyCourses()" data-testid="learner-modal-access-denied-back-btn">
                  <app-icon name="arrow_back" size="sm"></app-icon>
                  Back to My Courses
                </button>
              </div>
            </div>
          </div>
        }
        <!-- Error State -->
        @if (loadingError && !isLoading && !accessDenied) {
          <div class="error-state h-full flex items-center justify-center bg-base-200" data-testid="learner-modal-error-section">
            <div class="text-center max-w-md p-8">
              <app-icon [name]="entitlementBlockCode ? 'lock' : 'error'" class="text-error mb-4" size="lg"></app-icon>
              <h3 class="text-xl font-bold mb-2">{{ entitlementBlockCode ? entitlementHeading : 'Error Loading Course' }}</h3>
              <p class="text-base-content/70 mb-4">{{ loadingError }}</p>
              <!-- KAN-353: checkout-start failure (409s etc.) -->
              @if (checkoutError) {
                <div class="alert alert-error mb-4 text-left" data-testid="learner-modal-checkout-error">
                  <app-icon name="error" size="sm"></app-icon>
                  <span>{{ checkoutError }}</span>
                </div>
              }
              <!-- KAN-331 follow-up: show the price BEFORE Stripe checkout, not just after -->
              @if (entitlementBlockCode && entitlementPriceLabel) {
                <p class="text-lg font-bold mb-2" data-testid="learner-modal-entitlement-price">{{ entitlementPriceLabel }}</p>
              }
              <div class="flex gap-2 justify-center">
                <button class="btn btn-ghost" (click)="closeModal()" data-testid="learner-modal-error-close-btn">
                  <app-icon name="close" size="sm"></app-icon>
                  Close
                </button>
                @if (entitlementBlockCode) {
                  <!-- KAN-353: entitlement 403s get a purchase path, not Retry -->
                  <button class="btn btn-warning" [disabled]="checkoutBusy" (click)="onRenewAccess()" data-testid="learner-modal-renew-access-btn">
                    <app-icon name="shopping_cart" size="sm"></app-icon>
                    {{ entitlementCtaLabel }}
                  </button>
                } @else {
                  <button class="btn btn-primary" (click)="loadCourse()" data-testid="learner-modal-error-retry-btn">
                    <app-icon name="refresh" size="sm"></app-icon>
                    Retry
                  </button>
                }
              </div>
            </div>
          </div>
        }
        <!-- Course Landing Page -->
        @if (!isLoading && !loadingError && !accessDenied && showLandingPage && courseData) {
          <app-learner-landing-page
            [courseData]="courseData"
            [isReturningLearner]="isReturningLearner"
            [isCompleted]="isCourseComplete"
            [totalUnitsCount]="getTotalUnitsCount()"
            [totalModulesCount]="getTotalModulesCount()"
            [canSelfReset]="canLearnerSelfReset()"
            [resetsUsed]="courseData?.learnerResetCount || 0"
            [maxResets]="courseData?.maxLearnerResets"
            (onStart)="startCourse()"
            (onResume)="resumeCourse()"
            (onSkip)="skipToContent()"
            (onReset)="resetCourseProgress()"
            (onCloseModal)="closeModal()">
          </app-learner-landing-page>
        }
        <!-- Empty course (KAN-671): no units or modules, so there is no item
             to open once the landing page is dismissed (or skipped, in
             instructor preview). Say so instead of leaving the area blank. -->
        @if (!isLoading && !loadingError && !accessDenied && !showLandingPage && !currentItem && courseStructure && courseStructure.navigationItems.length === 0) {
          <div class="h-full flex items-center justify-center bg-base-200" data-testid="learner-modal-no-content">
            <div class="text-center max-w-md p-8">
              <app-icon name="school" size="lg" class="text-base-content/30 mb-4"></app-icon>
              <h3 class="text-xl font-bold mb-2">No course content available</h3>
              <p class="text-base-content/70">There are no units or modules to show for this course.</p>
            </div>
          </div>
        }
        <!-- Main Course Player (After landing page is dismissed) -->
        @if (!isLoading && !loadingError && !showLandingPage && currentItem) {
          <div
            class="player-container h-full">
            <!-- Debug: Module/Unit ID overlay -->
            @if (debug.isEnabled('moduleUnitIds') && currentItem) {
              <div class="flex flex-wrap gap-1 text-xs font-mono px-3 py-1 bg-base-200" data-testid="learner-modal-debug-ids">
                <span class="badge badge-ghost badge-xs">Item: {{ currentItem.id | slice:0:8 }}...</span>
                @if (currentItem.parentUnitId) {
                  <span class="badge badge-ghost badge-xs">UID: {{ currentItem.parentUnitId | slice:0:8 }}...</span>
                }
                <span class="badge badge-ghost badge-xs">CID: {{ data.courseId | slice:0:8 }}...</span>
                <span class="badge badge-ghost badge-xs">Type: {{ currentItem.type }}</span>
                <span class="badge badge-ghost badge-xs">Idx: {{ currentItemIndex }}</span>
              </div>
            }
            <!-- Split-Screen View -->
            <div class="split-screen-wrapper h-full">
              <!-- Unit Introduction -->
              @if (currentItem.type === 'unit_intro') {
                <app-learner-unit-intro
                  [currentItem]="currentItem"
                  [currentItemIndex]="currentItemIndex"
                  [totalItems]="courseStructure?.navigationItems.length || 1"
                  [canGoToPrevious]="canGoToPrevious"
                  [canGoToNext]="canGoToNext"
                  [isMobile]="isMobile"
                  [showTimeline]="showTimeline"
                  [isFullscreen]="isFullscreen"
                  [showFullscreen]="fullscreenAvailable"
                  [playerMode]="playerMode"
                  [unitStats]="getUnitStats()"
                  [lockReason]="getCurrentUnitGateLock()"
                  [unitResult]="getCurrentUnitResult()"
                  (onNavigatePrevious)="navigateToPrevious()"
                  (onNavigateNext)="navigateToNext()"
                  (onTimelineToggle)="toggleTimeline()"
                  (onFullscreenToggle)="toggleFullscreen()"
                  (onCloseModal)="closeModal()">
                </app-learner-unit-intro>
                <!-- KAN-314 VS2/VS3: live unit aggregate + pass/fail readout.
                     KAN-370: now rendered INSIDE the intro card
                     (app-learner-unit-intro [unitResult]); the old block here
                     was a sibling below the full-height component and got
                     clipped a full viewport below the fold by the content
                     area's overflow:hidden - exactly the KAN-326 bug class.
                     KAN-326: the locked-unit reason likewise renders INSIDE the
                     intro card (app-learner-unit-intro [lockReason]). -->
              }
              <!-- Regular Module with Video and/or Quiz -->
              <!-- KAN-318 VS4: progression-gate lock - block content of a unit whose prior gated unit isn't passed -->
              @if ((currentItem.type === 'module' || currentItem.type === 'unit_quiz') && getCurrentUnitGateLock()) {
                <div class="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="unit-gate-locked">
                  <app-icon name="lock" size="lg" class="text-warning mb-4"></app-icon>
                  <h3 class="text-lg font-semibold mb-2">Unit Locked</h3>
                  <p class="text-base-content/70 max-w-md">{{ getCurrentUnitGateLock() }}</p>
                </div>
              }
              @if ((currentItem.type === 'module' || currentItem.type === 'unit_quiz') && !getCurrentUnitGateLock()) {
                <app-split-screen-player
                  [itemType]="currentItem.type === 'unit_quiz' ? 'unit_quiz' : 'module'"
                  [videoUrl]="getCurrentVideoInfo().videoUrl || ''"
                  [videoId]="getCurrentVideoInfo().videoId"
                  [cdnHostname]="getCurrentVideoInfo().cdnHostname || ''"
                  [moduleId]="currentItem.id"
                  [courseId]="data.courseId"
                  [quizQuestions]="getCurrentQuizQuestions()"
                  [quizDescription]="currentItem.quizDescription || ''"
                  [quizId]="getQuizIdForCurrentModule()"
                  [quizCompleted]="isQuizCompleted()"
                  [moduleTitle]="currentItem.name"
                  [isVideoRequired]="playerConfig.enforceVideoCompletion && currentItem.hasVideo && (currentItem.requiredWatchPercentage ?? 90) > 0"
                  [requiredWatchPercentage]="currentItem.requiredWatchPercentage ?? 90"
                  [passingScore]="currentItem.passingScore ?? 80"
                  [feedbackVisibility]="currentItem.feedbackVisibility || 'indicators_answers'"
                  [feedbackFlags]="currentItem.feedbackFlags"
                  [revealAnswersOnlyOnPass]="currentItem.revealAnswersOnlyOnPass || false"
                  [customIncorrectMessage]="currentItem.wrongAnswerFeedback || null"
                  [customPassMessage]="currentItem.passFeedback || null"
                  [mode]="playerMode"
                  [playerConfig]="playerConfig"
                  [isModal]="true"
                  [showCloseButton]="true"
                  [showNavigation]="true"
                  [canGoToPrevious]="canGoToPrevious"
                  [canGoToNext]="canGoToNext"
                  [isLastItem]="isLastNavigableItem"
                  [completionPending]="completionPending"
                  [currentItemIndex]="currentItemIndex"
                  [totalItems]="courseStructure?.navigationItems.length || 1"
                  [showTimelineToggle]="true"
                  [timelineVisible]="showTimeline"
                  [moduleProgress]="getModuleProgress(currentItem.id)"
                  [useServerScoring]="playerMode === 'learner'"
                  [quizSubmissionError]="quizSubmissionError"
                  [quizSubmissionErrorInfo]="quizSubmissionErrorInfo"
                  (onQuizSubmit)="handleQuizSubmit($event)"
                  (onComplete)="onModuleComplete()"
                  (onProgressUpdate)="onProgressUpdate($event)"
                  (onNavigatePrevious)="navigateToPrevious()"
                  (onNavigateNext)="navigateToNext()"
                  (onTimelineToggle)="toggleTimeline()"
                  (onClose)="closeModal()">
                </app-split-screen-player>
              }
              <!-- Course Summary (All Modules Complete). Suppressed when lockToUnit=true (Wave 2). -->
              @if (shouldRenderCourseSummary()) {
                <!-- KAN-577 B: the aggregate alert and the certification panel
                     are SIBLINGS of a height:100% summary component inside a
                     height:100% wrapper whose ancestor is overflow:hidden, so
                     their height pushed the bottom of the summary - the
                     certificate card and the Exit/Review actions - past the
                     clip and out of reach. Same shape as the KAN-370 and
                     KAN-326 clips noted above. This flex column gives the
                     siblings their own height and hands the summary the
                     leftover space instead of a second full 100%. -->
                <div class="course-summary-branch">
                <!-- KAN-319 VS5: course-wide aggregate pass/fail readout.
                     KAN-411 round 4: the VERDICT is the OVERALL fold (per-unit
                     floor/gate/aggregate + course aggregate), not the scoped
                     courseResult.passed - which claimed "Passed" in green while
                     the points floor blocked completion. The % stays the course
                     aggregate, and the reason line names what blocks it. The
                     applicability condition is unchanged: with no
                     coursePassingThreshold there is no aggregate to report. -->
                @if (courseResult?.applicable) {
                  <div class="px-4 pt-4" data-testid="course-aggregate">
                    <div class="alert" [class]="courseVerdictAlertClass">
                      <div>
                        <span class="font-medium" data-testid="course-aggregate-line">{{ courseVerdictLine }}</span>
                        @if (courseVerdictBlockReason) {
                          <div class="text-sm mt-1" data-testid="course-block-reason">{{ courseVerdictBlockReason }}</div>
                        }
                      </div>
                    </div>
                  </div>
                }
                @if (playerMode === 'learner' && data.courseId) {
                  <div class="px-4 pt-4">
                    <app-certification-status-panel
                      [courseId]="data.courseId"
                      [compact]="true">
                    </app-certification-status-panel>
                  </div>
                }
                <app-learner-course-summary
                  [courseData]="courseData"
                  [courseStructure]="courseStructure"
                  [courseProgressMap]="courseProgressMap"
                  [unitResultsMap]="unitResultsMap"
                  [courseComplete]="isCourseComplete"
                  [currentItemIndex]="currentItemIndex"
                  [totalItems]="courseStructure?.navigationItems.length || 1"
                  [canGoToPrevious]="canGoToPrevious"
                  [canGoToNext]="canGoToNext"
                  [isMobile]="isMobile"
                  [showTimeline]="showTimeline"
                  [isFullscreen]="isFullscreen"
                  [showFullscreen]="fullscreenAvailable"
                  [playerMode]="playerMode"
                  (onNavigatePrevious)="navigateToPrevious()"
                  (onNavigateNext)="navigateToNext()"
                  (onTimelineToggle)="toggleTimeline()"
                  (onFullscreenToggle)="toggleFullscreen()"
                  (onCloseModal)="closeModal()"
                  (onOpenCertificate)="openCertificate($event)">
                </app-learner-course-summary>
                </div>
              }
            </div>
          </div>
        }
      </div>
      </div>
    </div>
  }
</div>

<!-- Close Confirmation Dialog -->
@if (showCloseConfirmation) {
  <div
    class="fixed inset-0 z-[10000] flex items-center justify-center bg-base-content/50"
    role="dialog"
    aria-modal="true"
    aria-label="Confirm exit"
    data-testid="learner-modal-close-confirm-modal"
    [@fadeIn]
    (click)="isCourseComplete ? confirmClose() : cancelClose()">
    <div class="bg-base-100 rounded-lg shadow-xl p-6 max-w-md mx-4"
      (click)="$event.stopPropagation()">
      <!-- Course Complete: Success dialog -->
      @if (isCourseComplete) {
        <div class="flex items-start gap-4 mb-4">
          <div class="flex-shrink-0">
            <app-icon name="check_circle" size="lg" class="text-success"></app-icon>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-bold mb-2">Course Complete!</h3>
            <p class="text-base-content/70">
              Congratulations! Your progress and results have been saved.
            </p>
          </div>
        </div>
        <div class="flex gap-3 justify-end">
          <button class="btn btn-success" (click)="confirmClose()" data-testid="learner-modal-exit-success-btn">
            <app-icon name="check" size="sm"></app-icon>
            Exit Course
          </button>
        </div>
      } @else {
        <div class="flex items-start gap-4 mb-4">
          <div class="flex-shrink-0">
            <app-icon name="warning" size="lg" class="text-warning"></app-icon>
          </div>
          <div class="flex-1">
            <h3 class="text-lg font-bold mb-2">Exit Course?</h3>
            <p class="text-base-content/70">
              Your progress has been saved, but you'll need to restart from where you left off.
            </p>
          </div>
        </div>
        <!-- KAN-691: wraps so the two non-shrinking buttons cannot push
             "Stay in Course" off a 320px screen. -->
        <div class="flex flex-wrap gap-3 justify-end">
          <button class="btn btn-ghost" (click)="cancelClose()" data-testid="learner-modal-stay-btn">
            Stay in Course
          </button>
          <button class="btn btn-error" (click)="confirmClose()" data-testid="learner-modal-exit-btn">
            <app-icon name="exit_to_app" size="sm"></app-icon>
            Exit Course
          </button>
        </div>
      }
      <!-- In-Progress: Warning dialog -->
    </div>
  </div>
}

<!-- Keyboard Shortcuts Help (Hidden, for screen readers) -->
<div class="sr-only" aria-live="polite">
  Course Player: Use arrow keys to navigate, T to toggle timeline, Escape to close
</div>
